import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../components/Button';
import { useToast } from '../../store/ToastContext';
import { formatDate, formatDateTime, formatNumber } from '../../utils/helpers';
import { manualRegistrationsApi } from './manualRegistrationsApi';
import { changeTypeLabel, formatFileSize, formatINR, methodLabel } from './manualRegistrationUtils';
import {
  ReceiptModal, RegistrationStatusBadge, SubscriptionHistory,
} from './ManualRegistrationParts';
import { SectionHeading } from './AdminWidgets';
import { ActivityIcon, EyeOpenIcon, FileTextIcon, ReceiptIcon, UsersIcon } from './ProductAdminIcons';
import styles from './ProductAdmin.module.css';
import ui from './ProductAdminDashboard.module.css';
import t from './AdminTable.module.css';
import m from './ManualRegistrations.module.css';

function Fact({ label, children }) {
  return (
    <div className={m.fact}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// One manual registration: payment, receipt, cancellation and the company's subscription history
export default function ProductAdminManualRegistrationDetail() {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [registration, setRegistration] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');

  const load = useCallback(async () => {
    setError(''); setHistoryError('');
    try {
      const found = await manualRegistrationsApi.getRegistration(registrationId);
      setRegistration(found);
      manualRegistrationsApi.companyHistory(found.company_id)
        .then(setHistory)
        .catch((err) => setHistoryError(err.response?.data?.error || 'Unable to load the subscription history'));
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load this registration');
    } finally { setLoading(false); }
  }, [registrationId]);

  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (cancelReason.trim().length < 5) { setCancelError('Give the reason for cancelling (at least 5 characters)'); return; }
    setCancelling(true); setCancelError('');
    try {
      await manualRegistrationsApi.cancelRegistration(registration.id, cancelReason.trim());
      toast.success(`${registration.registration_no} cancelled`);
      setCancelOpen(false); setCancelReason('');
      await load();
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Could not cancel the registration');
    } finally { setCancelling(false); }
  };

  // The receipt is private: ask the API for it when it is opened
  const openReceipt = async (registrationId, meta) => {
    setReceipt({ ...meta, loading: true });
    try {
      const url = await manualRegistrationsApi.receiptUrl(registrationId);
      setReceipt({ ...meta, url, keepUrl: !String(url || '').startsWith('blob:') });
    } catch (err) {
      setReceipt({ ...meta, error: err.response?.data?.error || 'The receipt could not be loaded' });
    }
  };

  const showHistoryReceipt = (item) => openReceipt(item.registration_id, {
    name: `Receipt — ${item.reference || registration?.registration_no || ''}`,
    type: item.registration_id === registration?.id ? registration.proof?.type : undefined,
  });

  if (loading) {
    return <div className={ui.container}><p className={styles.loading}>Loading…</p></div>;
  }
  if (error || !registration) {
    return (
      <div className={ui.container}>
        <div className={styles.error}>{error || 'Registration not found'}</div>
        <div><Button variant="secondary" onClick={() => navigate('/product-admin/manual-registrations')}>Back to list</Button></div>
      </div>
    );
  }

  const proof = registration.proof;
  const online = registration.source === 'online';   // paid through the application: shown, not editable
  return (
    <div className={ui.container}>
      <header className={m.headerRow}>
        <div className={ui.pageHeader}>
          <h1 className={ui.title}>{online ? 'Online payment' : registration.registration_no}</h1>
          <p className={ui.subtitle}>
            {registration.company_name} · <RegistrationStatusBadge status={registration.status} />
          </p>
        </div>
        <div className={m.headerActions}>
          <Button variant="secondary" onClick={() => navigate('/product-admin/manual-registrations')}>Back to list</Button>
        </div>
      </header>

      {registration.status === 'cancelled' && (
        <div className={`${m.banner} ${m.bannerWarn}`}>
          Cancelled on {formatDateTime(registration.cancelled_at)}. Reason: {registration.cancel_reason}
        </div>
      )}

      <section className={ui.panel}>
        <SectionHeading tone="blue" icon={<UsersIcon />} title={online ? 'Subscription' : 'Registration'}
          text="Company, plan and subscription period." />
        <dl className={m.facts}>
          <Fact label="Company">{registration.company_name}</Fact>
          <Fact label="Administrator">{registration.admin.name}<br /><span className={t.muted}>{registration.admin.email}</span></Fact>
          <Fact label="Type">{changeTypeLabel(registration.change_type)}</Fact>
          <Fact label="Plan">{registration.plan_name}{registration.previous_plan_name ? ` (was ${registration.previous_plan_name})` : ''}</Fact>
          <Fact label="Licences">{formatNumber(registration.licences)}</Fact>
          <Fact label="Document limit">{formatNumber(registration.document_limit)}</Fact>
          <Fact label="Subscription period">{formatDate(registration.start_date)} → {formatDate(registration.expiry_date)}</Fact>
          {online ? (
            <>
              <Fact label="Documents used">{formatNumber(registration.documents_used_snapshot)}</Fact>
              <Fact label="Paid by">{registration.created_by}<br /><span className={t.muted}>{formatDateTime(registration.created_at)}</span></Fact>
            </>
          ) : (
            <>
              <Fact label="Licence tokens moved">{formatNumber(registration.tokens_moved)}</Fact>
              <Fact label="Documents used before">{formatNumber(registration.documents_used_snapshot)}</Fact>
              <Fact label="Registered by">{registration.created_by}<br /><span className={t.muted}>{formatDateTime(registration.created_at)}</span></Fact>
              <Fact label="Customer email">{registration.send_email ? 'Confirmation and invoice sent' : 'Not sent (turned off)'}</Fact>
            </>
          )}
        </dl>
      </section>

      <section className={ui.panel}>
        <SectionHeading tone="amber" icon={<ReceiptIcon />} title="Payment"
          text={online ? 'Paid by the customer through the application.' : 'What was received and how.'} />
        <dl className={m.facts}>
          <Fact label="Amount received (incl. GST)">{formatINR(registration.amount)}</Fact>
          {!online && <Fact label="List price">{formatINR(registration.list_price)}</Fact>}
          <Fact label="Payment method">{methodLabel(registration.payment_method)}</Fact>
          <Fact label="Reference">{registration.payment_reference}</Fact>
          <Fact label="Payment date">{formatDate(registration.payment_date)}</Fact>
          {registration.price_override_reason && <Fact label="Price override reason">{registration.price_override_reason}</Fact>}
          {registration.remarks && <Fact label="Remarks">{registration.remarks}</Fact>}
        </dl>

        <div className={m.spacer} />
        {proof ? (
          <div className={m.fileChip}>
            <span className={m.fileThumb}>{proof.type === 'application/pdf' ? 'PDF' : 'IMG'}</span>
            <span className={m.fileInfo}>
              <strong title={proof.name}>{proof.name}</strong>
              <span>{formatFileSize(proof.size)}{proof.sha256 ? ` · SHA-256 ${proof.sha256.slice(0, 12)}…` : ''} · stored privately</span>
            </span>
            <Button size="sm" variant="secondary" className={t.softButton} onClick={() => openReceipt(registration.id, { name: proof.name, type: proof.type })}>
              <span className={m.buttonContent}><EyeOpenIcon size={14} /> View receipt</span>
            </Button>
          </div>
        ) : (
          <div className={m.timelineNote}>
            {online
              ? 'Paid online: the receipt and GST invoice are in the customer’s payment history.'
              : 'No receipt was uploaded.'}
          </div>
        )}
      </section>

      {!online && registration.status === 'active' && (
        <section className={ui.panel}>
          <SectionHeading tone="rose" icon={<ActivityIcon />} title="Cancel registration"
            text="For correcting a registration made by mistake." />
          {!registration.can_cancel && (
            <div className={m.timelineNote}>{registration.cancel_blocked_reason}</div>
          )}
          {registration.can_cancel && !cancelOpen && (
            <div>
              <Button variant="danger" size="sm" onClick={() => setCancelOpen(true)}>Cancel this registration</Button>
            </div>
          )}
          {registration.can_cancel && cancelOpen && (
            <div className={m.dangerZone}>
              <div className={m.help}>
                The subscription created by this registration is cancelled and the previous subscription and licence tokens are restored.
                An invoice that was already issued is not voided.
              </div>
              <div className={m.field}>
                <label className={m.label} htmlFor="cancel-reason">Reason<span className={m.required}>*</span></label>
                <textarea id="cancel-reason" className={`${m.textarea} ${cancelError ? m.invalid : ''}`} value={cancelReason}
                  onChange={(e) => { setCancelReason(e.target.value); setCancelError(''); }} />
                {cancelError && <span className={m.errorText} role="alert">{cancelError}</span>}
              </div>
              <div className={m.actions}>
                <Button variant="secondary" size="sm" onClick={() => { setCancelOpen(false); setCancelError(''); }} disabled={cancelling}>Keep it</Button>
                <Button variant="danger" size="sm" onClick={cancel} loading={cancelling}>Confirm cancellation</Button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className={ui.panel}>
        <SectionHeading tone="purple" icon={<FileTextIcon />} title="Subscription & Payment History"
          text={`All subscriptions of ${registration.company_name}, newest first.`} />
        <SubscriptionHistory items={history} loading={!history && !historyError} error={historyError} onViewReceipt={showHistoryReceipt} />
      </section>

      <ReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
